import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  toast,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { MessageCircleIcon, SkipForwardIcon, SparklesIcon } from "lucide-react";
import { useMemo } from "react";
import {
  useCampEventsQuery,
  useCampEventQuery,
  useCampWaitlistQuery,
  usePromoteWaitlistMutation,
  useSkipWaitlistMutation,
} from "../api/queries";

const ALL_CLASSES_FILTER = "__all__";

const WAITLIST_STATUS_LABELS: Record<string, string> = {
  waiting: "ລໍຖ້າ",
  offered: "ເສນແລ້ວ",
  accepted: "ຍອມຮັບ",
  expired: "ໝົດເວລາ",
  cancelled: "ຍົກເລີກ",
  skipped: "ຂ້າມ",
};

export function CampWaitlistPage() {
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as { classTypeId?: string };
  const canUpdate = useActionPermission(["camp-registrations:update"]);
  const { data: eventsData } = useCampEventsQuery();
  const event = eventsData?.items[0];
  const { data: detail } = useCampEventQuery(event?.id);
  const { data, isLoading, refetch } = useCampWaitlistQuery(
    event?.id,
    search.classTypeId,
  );
  const promoteMutation = usePromoteWaitlistMutation();
  const skipMutation = useSkipWaitlistMutation();

  const classFilterOptions = useMemo(() => {
    const options = (detail?.classOptions ?? []).map((opt) => ({
      value: opt.classTypeId,
      label: opt.publicLabelLao?.trim() || opt.classTypeName || opt.classTypeId,
    }));
    return [{ value: ALL_CLASSES_FILTER, label: "ທຸກຄລາສ" }, ...options];
  }, [detail?.classOptions]);

  const handlePromote = async (id: string, name: string) => {
    const ok = await confirm({
      title: "ເສນີທີ່ນັ່ງ",
      description: `ເສນີທີ່ນັ່ງໃຫ້ ${name}?`,
      actionText: "ເສນີ",
    });
    if (!ok) return;
    try {
      await promoteMutation.mutateAsync(id);
      toast.success("ເສນີທີ່ນັ່ງແລ້ວ — ແຈ້ງເຕືອນ staff ແລ້ວ");
      await refetch();
    } catch {
      toast.error("ເສນີບໍ່ສຳເລັດ (ອາດບໍ່ມີທີ່ນັ່ງ)");
    }
  };

  const handleSkip = async (id: string, name: string) => {
    const ok = await confirm({
      title: "ຂ້າມຄິວ",
      description: `ຂ້າມ ${name} ແລະເສນີຄົນຖັດໄປ?`,
      actionText: "ຂ້າມ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await skipMutation.mutateAsync(id);
      toast.success("ຂ້າມຄິວແລ້ວ");
      await refetch();
    } catch {
      toast.error("ຂ້າມຄິວບໍ່ສຳເລັດ");
    }
  };

  const items = data?.items ?? [];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ໃບສະໝັກ Camp", to: "/app/camp-registrations" },
            { label: "ຄິວສຳຮອງ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              ຄິວສຳຮອງ Camp
            </h1>
            <p className="text-muted-foreground text-sm">
              ຈັດການຄິວເມື່ອຄລາສເຕັມ — ເສນີທີ່ນັ່ງ ແລະ ຕິດຕໍ່ຜ່ານ WhatsApp
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/camp-events" })}
          >
            ຕັ້ງຄ່າ Capacity
          </Button>
        </div>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <SparklesIcon className="size-5" />
              ລາຍການຄິວ
            </CardTitle>
            <div className="w-full sm:w-56">
              <SimpleSelect
                value={search.classTypeId ?? ALL_CLASSES_FILTER}
                onValueChange={(value) =>
                  nav({
                    to: "/app/camp-registrations/waitlist",
                    search:
                      value === ALL_CLASSES_FILTER
                        ? {}
                        : { classTypeId: value },
                  })
                }
                options={classFilterOptions}
                placeholder="ກັ່ນຕາມຄລາສ"
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
            ) : items.length === 0 ? (
              <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີຄິວສຳຮອງ</p>
            ) : (
              <div className="space-y-3">
                {items.map((item) => {
                  const classLabel =
                    item.labelLao?.trim() || item.className || item.classTypeId;
                  const displayName = item.nickname?.trim()
                    ? `${item.nickname} (${item.fullName})`
                    : item.fullName;

                  return (
                    <div
                      key={item.id}
                      className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline">#{item.position}</Badge>
                          <Badge>{classLabel}</Badge>
                          <Badge variant="secondary">
                            {WAITLIST_STATUS_LABELS[item.status] ?? item.status}
                          </Badge>
                        </div>
                        <p className="font-medium">{displayName}</p>
                        <p className="text-muted-foreground text-sm">
                          {item.phoneNumber}
                        </p>
                        {item.offerExpiresAt ? (
                          <p className="text-muted-foreground text-xs">
                            ໝົດເວລາເສນີ: {item.offerExpiresAt}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {item.whatsappUrl ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              window.open(item.whatsappUrl!, "_blank", "noopener")
                            }
                          >
                            <MessageCircleIcon className="mr-2 size-4" />
                            ສົ່ງ WhatsApp
                          </Button>
                        ) : null}
                        {canUpdate && item.status === "waiting" ? (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handlePromote(item.id, displayName)}
                            disabled={promoteMutation.isPending}
                          >
                            ເສນີທີ່ນັ່ງ
                          </Button>
                        ) : null}
                        {canUpdate &&
                        ["waiting", "offered"].includes(item.status) ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSkip(item.id, displayName)}
                            disabled={skipMutation.isPending}
                          >
                            <SkipForwardIcon className="mr-2 size-4" />
                            ຂ້າມ
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
